import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Bot, User } from "lucide-react";
import { API, waLink } from "@/lib/api";

const QUICK_QUESTIONS = [
  "Come faccio a pagare i debiti?",
  "Quanto tempo ci metterò?",
  "Cosa succede ai garanti?",
  "Mi portano via la casa?",
];

const WELCOME = {
  role: "assistant",
  content:
    "Ciao! Sono l'assistente dell'Associazione Vittime del Debito. Il debito non è una colpa: noi siamo dalla tua parte. Posso risponderti su debiti, saldo e stralcio, pignoramenti e diritti del debitore, e indicarti come iscriverti gratis o prenotare un colloquio. Come posso aiutarti?",
};

function getSessionId() {
  let id = localStorage.getItem("avd-chat-session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("avd-chat-session", id);
  }
  return id;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setInput("");
    setLoading(true);
    setMessages((prev) => [...prev, { role: "user", content: message }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let done = false;
      while (!done) {
        const { done: d, value } = await reader.read();
        if (d) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") {
            done = true;
            break;
          }
          try {
            const json = JSON.parse(payload);
            if (json.delta) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: copy[copy.length - 1].content + json.delta,
                };
                return copy;
              });
            }
            if (json.error) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: "assistant", content: json.error };
                return copy;
              });
            }
          } catch {
            /* chunk incompleto */
          }
        }
      }
    } catch {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Connessione non riuscita. Riprova tra poco oppure scrivici su WhatsApp.",
        };
        return copy;
      });
    }
    setLoading(false);
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen(!open)}
        data-testid="chat-widget-toggle"
        aria-label={open ? "Chiudi assistente" : "Apri assistente virtuale"}
        className="fixed bottom-6 right-6 z-[60] w-16 h-16 rounded-full bg-brand-blue text-white shadow-2xl shadow-brand-blue/40 flex items-center justify-center"
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
      >
        {open ? <X className="w-7 h-7" /> : <MessageCircle className="w-7 h-7" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed bottom-24 right-4 sm:right-6 z-[60] w-[calc(100vw-2rem)] max-w-md h-[560px] max-h-[70vh] bg-white rounded-3xl shadow-2xl shadow-brand-ink/20 border border-slate-100 flex flex-col overflow-hidden"
          >
            <div className="bg-brand-blue text-white px-5 py-4 flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </span>
              <div className="flex-1">
                <p className="font-bold leading-tight">Assistente dell'Associazione</p>
                <p className="text-xs text-blue-100">Risponde in pochi secondi — gratis</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-brand-mist/50" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && (
                    <span className="w-8 h-8 rounded-full bg-brand-aqua text-white flex items-center justify-center shrink-0 mt-1">
                      <Bot className="w-4 h-4" />
                    </span>
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-brand-blue text-white rounded-br-md"
                        : "bg-white text-brand-ink shadow-sm border border-slate-100 rounded-bl-md"
                    }`}
                  >
                    {m.content || (loading && i === messages.length - 1 ? "…" : "")}
                  </div>
                  {m.role === "user" && (
                    <span className="w-8 h-8 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center shrink-0 mt-1">
                      <User className="w-4 h-4" />
                    </span>
                  )}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2 bg-brand-mist/50">
                {QUICK_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    data-testid={`chat-quick-${q.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="text-sm font-semibold text-brand-blue bg-white border border-brand-blue/30 rounded-full px-3 py-1.5 hover:bg-brand-blue hover:text-white transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            <div className="p-3 border-t border-slate-100 bg-white">
              <div className="flex gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Scrivi la tua domanda…"
                  data-testid="chat-input"
                  className="flex-1 rounded-full border-2 border-slate-200 px-4 h-12 text-base focus:border-brand-blue focus:outline-none"
                />
                <button
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  data-testid="chat-send-button"
                  aria-label="Invia messaggio"
                  className="w-12 h-12 rounded-full bg-brand-aqua text-white flex items-center justify-center disabled:opacity-40 transition-transform hover:scale-105"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
              <a
                href={waLink("Buongiorno, ho bisogno di parlare con un operatore dell'Associazione Vittime del Debito.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="chat-whatsapp-button"
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-bold h-11 text-sm"
              >
                <MessageCircle className="w-4 h-4" />
                Preferisci parlare con noi? Scrivici su WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
